/**
 * Pult — the screen of the pult: a copy of a Rane System One over the whole app (task 54).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Решение», «Экран, раскладка, поворот», «Лупа
 * и увеличение»; stories 151–157. Opened from «Сейчас играет» by «Пульт» in its header and drawn
 * over everything, as that screen is; «Свернуть» gives the turntable back. Opening and closing
 * alone change no sound: the player hears only the controls (tasks 55–57), and while the pult is
 * open both decks' tracks are kept ready for the scratch (task 57).
 *
 * ## Which board
 *
 * The phone on its side gets the board on its side (780 × 390), upright the board upright
 * (390 × 780) — the phone's own `(orientation: landscape)`, followed as it turns; the orientation
 * is never locked (the owner's choice). Opening asks Telegram for the whole screen (8.0): on its
 * side Telegram shows a Mini App only so. Closing gives it back — only if it was asked for here.
 * A client that cannot ask has the pult upright in the window it has, however the phone is held.
 *
 * ## The black bands and the magnifier
 *
 * The board fits the stage whole, by its smaller side (`styles/pult.css`), with a band a finger
 * wide either side of it — left and right on its side, above and below upright. The bands hold
 * «Свернуть» and the magnifier twice, one under each thumb; they are not on the board, which is a
 * copy of the device and has no key of ours on it. A band rather than a key over the board's
 * corner: on a small phone the board meets the screen's edge, and a key there would cover a
 * control.
 *
 * ## Two states
 *
 * **play** — no layer over the board: every finger is a control's (`components/PultBoard.tsx`),
 * and a finger never moves the picture. **zoom** — after the magnifier: the board dims, its
 * controls sleep, and a transparent layer over the stage takes the fingers — two spread the
 * picture, one moves it (`lib/pult-zoom.ts`). The magnifier again: the board wakes in the window
 * chosen. A double tap on it — the whole board, in the state it was in. The window is remembered
 * for each orientation (`store/pult.ts`) and comes back at the next opening.
 *
 * ## The pads (task 60)
 *
 * Each deck's mode key chooses its pads' mode, PARAMETER turns the mode's pages, the screen over
 * the pads names them (`lib/pult-pads.ts`). In HOT CUE a pad jumps to its cue, sets one on a pause
 * and takes it away on a long press; the cues are the listener's on the server (`store/cues.ts`),
 * read each time a deck takes a track while the pult is open — and so at every opening, which is
 * how a phone sees what another wrote since.
 *
 * ## Loops, ROLL, BEAT JUMP (task 62)
 *
 * The deck's voice plays them (`engine/scratch.ts`, «Loops»), and only a deck that can loop lights
 * its ROLL and LOOP pads: its track whole in memory, and the deck playing. The pult gets both decks'
 * tracks ready for the voice even when the phone asks not to be moved: a loop is sound, not motion
 * — only the platter's gesture sleeps then. LOOP ON/OFF sets a loop of {@link LOOP_BEATS} beats
 * from where the deck is, or lets the one on go; held, RELOOP — the deck's last loop before the
 * finger landed, from its start. ½ and 2× halve and double the loop on; with none on they do
 * nothing — a length nobody sees is a surprise at the next LOOP. A ROLL pad repeats its length
 * while held, the latest pad held deciding, and lets the track go on where it would be when the
 * last finger lifts — or when the pult closes under it. A LOOP pad sets a loop of its length,
 * another pad changes it, the same pad lets it go. No roll over a loop, no loop over a roll, RELOOP
 * included. BEAT JUMP moves the deck JUMP SIZE beats: its voice and loop while it loops, its element
 * on any track otherwise. A HOT CUE, CUE or BEAT JUMP of the element first ends a loop or the
 * hand-over after one (`ScratchController.letGo`): the hand-over would take the element back.
 */

import type { JSX, PointerEvent } from 'react';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

import { PultBoard } from '../components/PultBoard';
import type { PultLights } from '../components/PultBoard';
import { PultScreen } from '../components/PultScreen';
import { player } from '../engine/player';
import { scratch } from '../engine/scratch';
import { telegram } from '../lib/telegram';
import { PULT_EFFECTS } from '../lib/pult-effects';
import { PAD_MODES, PULT_LAYOUT, SCREEN_VIEWS } from '../lib/pult-layout';
import type { DeckSide, PultOrientation } from '../lib/pult-layout';
import {
  LOOP_BEATS,
  PAD_COUNT,
  beatSecondsOf,
  hotCuePress,
  jumpBeatsAt,
  litPads,
  loopBeatsTimes,
  padBeats,
  padCells,
} from '../lib/pult-pads';
import { boardTransform, createZoomTracker } from '../lib/pult-zoom';
import type { PultFrame, PultPoint } from '../lib/pult-zoom';
import { clearCue, loadCues, setCue, useCues } from '../store/cues';
import { chooseRecord, setPitch, toggleKeylock, useDeck } from '../store/deck';
import {
  chooseEffect,
  choosePadMode,
  keepWindows,
  releaseAll,
  setWindow,
  showOnScreen,
  stopZoom,
  toggleAuto,
  togglePreset,
  toggleZoom,
  turnPadPage,
  usePult,
  valueOf,
  wholeWindow,
} from '../store/pult';
import { useScratch } from '../store/scratch';
import type { DeckLoop } from '../store/scratch';
import type { LoopAsked } from '../engine/scratch';
import '../styles/pult.css';

export const PULT_TITLE = 'Пульт';
export const PULT_CLOSE_LABEL = 'Свернуть пульт';
/** The magnifier in `play`: what a tap does. */
export const ZOOM_IN_LABEL = 'Лупа: увеличить пульт';
/** …and in `zoom`. */
export const ZOOM_OUT_LABEL = 'Лупа: играть';

/** The phone on its side. */
export const LANDSCAPE_QUERY = '(orientation: landscape)';

/** Two taps on the magnifier within this many milliseconds are a double tap. */
const DOUBLE_TAP_MS = 350;

function landscapeQuery(): MediaQueryList | undefined {
  const page: Partial<Pick<Window, 'matchMedia'>> = window;
  return page.matchMedia?.(LANDSCAPE_QUERY);
}

function subscribeOrientation(onChange: () => void): () => void {
  const query = landscapeQuery();
  query?.addEventListener('change', onChange);
  return () => {
    query?.removeEventListener('change', onChange);
  };
}

function readOrientation(): PultOrientation {
  return landscapeQuery()?.matches === true ? 'land' : 'port';
}

/** The way the phone is held, kept current. */
function useOrientation(): PultOrientation {
  return useSyncExternalStore(subscribeOrientation, readOrientation);
}

function MagnifierIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="pultIcon">
      <circle cx="10.5" cy="10.5" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M15 15l5.5 5.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function CollapseIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="pultIcon">
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** `A.play` → `['A', 'play']`; a key of the middle → `undefined`. */
function deckKey(id: string): readonly [DeckSide, string] | undefined {
  const side = id.slice(0, 2);
  if (side === 'A.') return ['A', id.slice(2)];
  if (side === 'B.') return ['B', id.slice(2)];
  return undefined;
}

/** `pad3` → 3; another key of a deck → `undefined`. */
function padNumber(name: string): number | undefined {
  const pad = Number(/^pad(\d)$/u.exec(name)?.[1]);
  return pad >= 1 && pad <= PAD_COUNT ? pad : undefined;
}

/** Deck `side`'s pad `pad`, as the board names it (`lib/pult-layout.ts`): `A.pad3`. */
function padId(side: DeckSide, pad: number): string {
  return `${side}.pad${String(pad)}`;
}

/**
 * Task 60: the pads whose finger landed on a cue, and that cue's track — a long press takes away
 * only a cue the pad had as the finger landed, never one the same press has just set.
 */
const landedOnCue = new Map<string, number>();

/** Task 62: one beat of deck `side`'s track, in seconds of the track. */
function beatSOf(side: DeckSide): number {
  return beatSecondsOf(useDeck.getState().decks[side].track?.beat);
}

/** Task 62: a loop of `beats` on deck `side`, as the voice is asked for it. */
function lengthOf(side: DeckSide, beats: number): Omit<LoopAsked, 'roll'> {
  return { lengthS: beats * beatSOf(side), beats };
}

/** Task 62: the ROLL pads held on each deck and their lengths, the latest last. */
const rolling: Record<DeckSide, { readonly pad: number; readonly beats: number }[]> = {
  A: [],
  B: [],
};

/** Task 62: what RELOOP plays — the deck's last loop as the LOOP key's finger landed. */
const reloopOf = new Map<DeckSide, DeckLoop | undefined>();

/** Task 62: a ROLL pad landed — the roll starts, or takes this pad's length. */
function pressRoll(side: DeckSide, pad: number, beats: number): void {
  const loop = useScratch.getState().loops[side];
  if (loop !== undefined && !loop.roll) return;
  if (loop === undefined) {
    if (scratch().loop(side, { ...lengthOf(side, beats), roll: true }) === undefined) return;
  } else {
    scratch().resize(side, lengthOf(side, beats));
  }
  telegram.haptic('impact');
  rolling[side] = [...rolling[side].filter((held) => held.pad !== pad), { pad, beats }];
}

/** Task 62: a ROLL pad lifted — the roll goes with the last of them, or takes the latest's length. */
function releaseRoll(side: DeckSide, pad: number): void {
  const before = rolling[side];
  const held = before.filter((one) => one.pad !== pad);
  if (held.length === before.length) return;
  rolling[side] = held;
  const latest = held.at(-1);
  if (latest === undefined) scratch().unloop(side);
  else scratch().resize(side, lengthOf(side, latest.beats));
}

/** Task 62: every roll's finger gone at once — the pult closed under them. */
function releaseRolls(): void {
  for (const side of ['A', 'B'] as const) {
    if (rolling[side].length === 0) continue;
    rolling[side] = [];
    scratch().unloop(side);
  }
}

/** Task 62: a LOOP pad — a loop of its length; another pad's length; the same pad lets it go. */
function pressLoopPad(side: DeckSide, beats: number): void {
  const loop = useScratch.getState().loops[side];
  if (loop?.roll === true) return;
  if (loop === undefined) {
    if (scratch().loop(side, { ...lengthOf(side, beats), roll: false }) === undefined) return;
  } else if (loop.beats === beats) {
    scratch().unloop(side);
  } else {
    scratch().resize(side, lengthOf(side, beats));
  }
  telegram.haptic('impact');
}

/** Task 62: LOOP ON/OFF — a loop of {@link LOOP_BEATS} from here, or the loop on let go. */
function pressLoopKey(side: DeckSide): void {
  reloopOf.set(side, scratch().lastLoop(side));
  const loop = useScratch.getState().loops[side];
  if (loop?.roll === true) return;
  if (loop !== undefined) {
    telegram.haptic('impact');
    scratch().unloop(side);
    return;
  }
  if (scratch().loop(side, { ...lengthOf(side, LOOP_BEATS), roll: false }) !== undefined) {
    telegram.haptic('impact');
  }
}

/** Task 62: RELOOP — the deck's last loop before LOOP ON/OFF's finger landed, not over a roll. */
function reloop(side: DeckSide): void {
  const last = reloopOf.get(side);
  reloopOf.delete(side);
  if (last === undefined || useScratch.getState().loops[side]?.roll === true) return;
  if (scratch().loopAt(side, last)) telegram.haptic('impact');
}

/** Task 62: ½ (`0.5`) and 2× (`2`) of the loop on; with none on, nothing. */
function loopTimes(side: DeckSide, factor: 0.5 | 2): void {
  const loop = useScratch.getState().loops[side];
  if (loop === undefined || loop.roll) return;
  telegram.haptic('selection');
  scratch().resize(side, lengthOf(side, loopBeatsTimes(loop.beats, factor)));
}

/** Task 62: BEAT JUMP, `direction` beats of JUMP SIZE — the voice and its loop, or the element. */
function beatJump(side: DeckSide, direction: -1 | 1): void {
  const deck = player().whereDeck(side);
  if (deck === undefined) return;
  const beats = jumpBeatsAt(valueOf(usePult.getState().values, `${side}.jumpsize`));
  const byS = direction * beats * beatSOf(side);
  telegram.haptic('impact');
  if (scratch().jumpLoop(side, byS)) return;
  scratch().letGo(side);
  player().jumpDeck(side, deck.positionS + byS);
}

/**
 * Task 60: a finger on pad `pad` of deck `side`, the key `id` — in HOT CUE, see `lib/pult-pads.ts`.
 * A deck whose cues are not read does nothing: the read is asked for again, which is how a read
 * the connection dropped comes back, and the next press acts. Task 62: ROLL and LOOP.
 */
function pressPad(id: string, side: DeckSide, pad: number): void {
  landedOnCue.delete(id);
  const { mode, page } = usePult.getState().pads[side];
  const beats = padBeats(mode, page, pad);
  if (mode === 'roll' && beats !== undefined) {
    pressRoll(side, pad, beats);
    return;
  }
  if (mode === 'loop' && beats !== undefined) {
    pressLoopPad(side, beats);
    return;
  }
  if (mode !== 'hotcue') return;
  const deck = player().whereDeck(side);
  if (deck === undefined) return;
  const cues = useCues.getState().byTrack[deck.trackId];
  if (cues === undefined) {
    void loadCues(deck.trackId);
    return;
  }
  const action = hotCuePress(cues, pad, deck);
  if (action.kind === 'none') return;
  telegram.haptic('impact');
  if (action.kind === 'jump') {
    landedOnCue.set(id, deck.trackId);
    // Task 62: a cue out of a loop, or of the hand-over after one — that goes first.
    scratch().letGo(side);
    player().jumpDeck(side, action.positionS);
    return;
  }
  setCue(deck.trackId, action.slot, action.positionS);
}

/**
 * Task 60: a finger held on a key — a pad's cue, if it landed on one, taken away. Task 62: LOOP
 * ON/OFF held — RELOOP.
 */
function holdKey(id: string): void {
  const own = deckKey(id);
  if (own?.[1] === 'loop') {
    reloop(own[0]);
    return;
  }
  const trackId = landedOnCue.get(id);
  const pad = own === undefined ? undefined : padNumber(own[1]);
  landedOnCue.delete(id);
  if (trackId === undefined || pad === undefined) return;
  telegram.haptic('impact');
  clearCue(trackId, pad);
}

/**
 * A key pressed — see `components/PultBoard.tsx`. Task 54: QUEUE, LIBRARY and VIEW. Task 55: the
 * decks' own keys and LOAD (`docs/superpowers/specs/2026-10-05-pult-design.md`, «Две равные деки»).
 * Task 60: the pads, their modes and PARAMETER.
 */
function pressKey(id: string): void {
  for (const [name] of SCREEN_VIEWS) {
    if (id === `screen.${name}`) showOnScreen(name);
  }
  if (id === 'auto') {
    telegram.haptic('impact');
    toggleAuto();
    return;
  }
  // Task 58: the four effect keys choose one, for both decks; PRESET is `clean` ↔ DUB OUT.
  for (const effect of PULT_EFFECTS) {
    if (id === `fx.${effect}`) {
      telegram.haptic('selection');
      chooseEffect(effect);
      return;
    }
  }
  if (id === 'preset') {
    telegram.haptic('impact');
    togglePreset();
    return;
  }
  if (id === 'load.A' || id === 'load.B') {
    const track = usePult.getState().chosen;
    if (track === undefined) return;
    telegram.haptic('impact');
    player().loadDeck(id === 'load.A' ? 'A' : 'B', track);
    return;
  }
  const own = deckKey(id);
  if (own === undefined) return;
  const [side, name] = own;
  const pad = padNumber(name);
  if (pad !== undefined) {
    pressPad(id, side, pad);
    return;
  }
  for (const [mode] of PAD_MODES) {
    if (name === `mode.${mode}`) {
      telegram.haptic('selection');
      choosePadMode(side, mode);
      return;
    }
  }
  switch (name) {
    case 'param.prev':
    case 'param.next':
      telegram.haptic('selection');
      turnPadPage(side, name === 'param.prev' ? -1 : 1);
      return;
    case 'play':
      telegram.haptic('impact');
      player().toggleDeck(side);
      return;
    case 'cue':
      telegram.haptic('impact');
      scratch().letGo(side);
      player().cueDeck(side);
      return;
    case 'rpm33':
    case 'rpm45':
      telegram.haptic('impact');
      chooseRecord(name === 'rpm33' ? '33' : '45', side);
      return;
    case 'reset':
      if (useDeck.getState().decks[side].pitch !== 0) telegram.haptic('selection');
      setPitch(0, side);
      return;
    case 'keylock':
      toggleKeylock(side);
      return;
    case 'bend.up':
      player().bendDeck(side, 1);
      return;
    case 'bend.down':
      player().bendDeck(side, -1);
      return;
    case 'echoout':
      telegram.haptic('impact');
      player().echoOut(side);
      return;
    case 'loop':
      pressLoopKey(side);
      return;
    case 'loop.half':
    case 'loop.double':
      loopTimes(side, name === 'loop.half' ? 0.5 : 2);
      return;
    case 'jump.back':
    case 'jump.fwd':
      beatJump(side, name === 'jump.back' ? -1 : 1);
      return;
    default:
      return;
  }
}

/** Task 56: MAIN's level for the meter. */
function mainLevel(): number {
  return player().level();
}

/** The last finger left a key: PITCH BEND lets go; task 62: a ROLL pad's roll. */
function releaseKey(id: string): void {
  const own = deckKey(id);
  if (own !== undefined && (own[1] === 'bend.up' || own[1] === 'bend.down')) {
    player().bendDeck(own[0], 0);
  }
  const pad = own === undefined ? undefined : padNumber(own[1]);
  if (own !== undefined && pad !== undefined) releaseRoll(own[0], pad);
}

export interface PultProps {
  readonly onClose: () => void;
}

export function Pult({ onClose }: PultProps): JSX.Element {
  const held = useOrientation();
  // On its side Telegram shows a Mini App only on the whole screen: a client that cannot give it
  // keeps the pult upright in the window it has. The browser of the development mode turns.
  const orientation = telegram.canRequestFullscreen() || !telegram.isReal ? held : 'port';
  const layout = PULT_LAYOUT[orientation];
  const view = usePult((s) => s.view);
  const win = usePult((s) => s.windows[orientation]);
  const screen = usePult((s) => s.screen);
  // Primitives, not the decks' objects: the player rewrites those twice a second.
  const sizeA = useDeck((s) => s.decks.A.size);
  const sizeB = useDeck((s) => s.decks.B.size);
  const playingA = useDeck((s) => s.decks.A.playing);
  const playingB = useDeck((s) => s.decks.B.playing);
  const keylockA = useDeck((s) => s.decks.A.keylock);
  const keylockB = useDeck((s) => s.decks.B.keylock);
  const trackA = useDeck((s) => s.decks.A.track);
  const trackB = useDeck((s) => s.decks.B.track);
  const auto = usePult((s) => s.auto);
  const effect = usePult((s) => s.effect);
  const preset = usePult((s) => s.preset);
  const padsA = usePult((s) => s.pads.A);
  const padsB = usePult((s) => s.pads.B);
  const idA = trackA?.id;
  const idB = trackB?.id;
  const cuesA = useCues((s) => (idA === undefined ? undefined : s.byTrack[idA]));
  const cuesB = useCues((s) => (idB === undefined ? undefined : s.byTrack[idB]));
  const readyA = useScratch((s) => s.byDeck.A);
  const readyB = useScratch((s) => s.byDeck.B);
  const loopA = useScratch((s) => s.loops.A);
  const loopB = useScratch((s) => s.loops.B);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  // A tracker for each spell of the magnifier: a finger still on the glass when the magnifier
  // was tapped never lifts for the layer that is gone, and must not haunt the next spell.
  const tracker = useMemo(() => (view === 'zoom' ? createZoomTracker() : undefined), [view]);
  const lastTap = useRef(0);

  useEffect(() => {
    closeRef.current?.focus();
    const asked = telegram.requestFullscreen();
    return () => {
      stopZoom();
      // A PITCH BEND held as the pult closes would never hear its release; nor a fader, nor a roll.
      player().bendDeck('A', 0);
      player().bendDeck('B', 0);
      releaseAll();
      releaseRolls();
      if (asked) telegram.exitFullscreen();
    };
  }, []);

  // Task 57: both platters take a finger, so both decks' tracks are got ready while the pult is
  // open — the turntable underneath holds only the active one, and only while it shows it. Task 62:
  // a phone that asked not to be moved has no gesture on a platter, but its loops are sound and
  // need the tracks all the same.
  useEffect(() => (trackA === undefined ? undefined : scratch().hold(trackA, 'A')), [trackA]);
  useEffect(() => (trackB === undefined ? undefined : scratch().hold(trackB, 'B')), [trackB]);

  // Task 60: each deck's cues, read as the deck takes its track — see «The pads».
  useEffect(() => {
    if (idA !== undefined) void loadCues(idA);
  }, [idA]);
  useEffect(() => {
    if (idB !== undefined) void loadCues(idB);
  }, [idB]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const lights = useMemo<PultLights>(() => {
    const lit = new Set<string>([
      `A.rpm${sizeA}`,
      `B.rpm${sizeB}`,
      `A.mode.${padsA.mode}`,
      `B.mode.${padsB.mode}`,
      `fx.${effect}`,
      `screen.${screen}`,
    ]);
    // Task 62: a deck can loop with its track ready for the voice and the deck playing.
    for (const pad of litPads(padsA.mode, cuesA, readyA && playingA)) lit.add(padId('A', pad));
    for (const pad of litPads(padsB.mode, cuesB, readyB && playingB)) lit.add(padId('B', pad));
    if (loopA !== undefined && !loopA.roll) lit.add('A.loop');
    if (loopB !== undefined && !loopB.roll) lit.add('B.loop');
    if (auto) lit.add('auto');
    if (preset === 'dubout') lit.add('preset');
    if (playingA) lit.add('A.play');
    if (playingB) lit.add('B.play');
    if (keylockA) lit.add('A.keylock');
    if (keylockB) lit.add('B.keylock');
    return {
      decks: {
        A: { record: sizeA, cells: padCells(padsA.mode, padsA.page, cuesA) },
        B: { record: sizeB, cells: padCells(padsB.mode, padsB.page, cuesB) },
      },
      lit,
    };
  }, [
    sizeA,
    sizeB,
    playingA,
    playingB,
    keylockA,
    keylockB,
    screen,
    auto,
    effect,
    preset,
    padsA,
    padsB,
    cuesA,
    cuesB,
    readyA,
    readyB,
    loopA,
    loopB,
  ]);

  /** The stage, and the board as it fits there whole. */
  const frame = (): { frame: PultFrame; left: number; top: number } | undefined => {
    const box = stageRef.current?.getBoundingClientRect();
    if (box === undefined || box.width <= 0 || box.height <= 0) return undefined;
    const fit = Math.min(box.width / layout.width, box.height / layout.height);
    return {
      frame: {
        width: box.width,
        height: box.height,
        boardWidth: layout.width * fit,
        boardHeight: layout.height * fit,
      },
      left: box.left,
      top: box.top,
    };
  };

  const point = (event: PointerEvent<HTMLElement>, left: number, top: number): PultPoint => ({
    x: event.clientX - left,
    y: event.clientY - top,
  });

  const fingerDown = (event: PointerEvent<HTMLDivElement>): void => {
    const at = frame();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Without capture the layer still has the finger while it stays over the stage.
    }
    tracker?.down(event.pointerId, point(event, at?.left ?? 0, at?.top ?? 0), win);
  };

  const fingerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const at = frame();
    if (at === undefined) return;
    const next = tracker?.move(event.pointerId, point(event, at.left, at.top), at.frame);
    if (next !== undefined) setWindow(orientation, next);
  };

  const fingerUp = (event: PointerEvent<HTMLDivElement>): void => {
    tracker?.up(event.pointerId, usePult.getState().windows[orientation]);
    keepWindows();
  };

  const magnifier = (): void => {
    const now = Date.now();
    const double = now - lastTap.current < DOUBLE_TAP_MS;
    lastTap.current = double ? 0 : now;
    toggleZoom();
    if (double) wholeWindow(orientation);
  };

  const zoomLabel = view === 'zoom' ? ZOOM_OUT_LABEL : ZOOM_IN_LABEL;

  return (
    <section
      className="pult"
      role="dialog"
      aria-modal="true"
      aria-label={PULT_TITLE}
      data-orientation={orientation}
      data-view={view}
    >
      <div ref={stageRef} className="pultStage">
        <PultBoard
          layout={layout}
          asleep={view === 'zoom'}
          lights={lights}
          onKey={pressKey}
          onKeyUp={releaseKey}
          onKeyHold={holdKey}
          screen={<PultScreen asleep={view === 'zoom'} />}
          transform={boardTransform(win)}
          level={mainLevel}
        />
        {view === 'zoom' && (
          <div
            className="pultGestures"
            data-testid="pult-gestures"
            onPointerDown={fingerDown}
            onPointerMove={fingerMove}
            onPointerUp={fingerUp}
            onPointerCancel={fingerUp}
          />
        )}
      </div>
      <button
        ref={closeRef}
        type="button"
        className="pultBandKey pultClose"
        aria-label={PULT_CLOSE_LABEL}
        onClick={onClose}
      >
        <CollapseIcon />
      </button>
      {(['start', 'end'] as const).map((place) => (
        <button
          key={place}
          type="button"
          className={`pultBandKey pultZoom pultZoom-${place}`}
          aria-label={zoomLabel}
          aria-pressed={view === 'zoom'}
          onClick={magnifier}
        >
          <MagnifierIcon />
        </button>
      ))}
    </section>
  );
}
